import React from 'react';
import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <div className="bg-gray-50">
      {/* Main Hero Section */}
      <main className="bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="max-w-3xl mx-auto">
            {/* Profile Image Container */}
            <div className="w-40 h-40 rounded-full mx-auto mb-8 shadow-lg overflow-hidden border-4 border-white">
              <img
                className="w-full h-full object-cover transform scale-150"
                src="/images/profile-sunnysjamil.jpg"
                alt="Profile of Sunny S. Jamil"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              Exploring the Intersection of Language &amp; Technology
            </h1>
            {/* Sub-headline / Introduction */}
            <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto">
              Welcome to my digital space. I'm a linguist fascinated by the structure of communication and a developer passionate about building clean, useful tools on the web. This site is a collection of my professional work, personal projects, and ongoing interests.
            </p>
            {/* Call to Action Buttons */}
            <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
              <Link
                to="/projects"
                className="w-full sm:w-auto bg-indigo-600 text-white font-semibold py-3 px-8 rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-300 ease-in-out transform hover:scale-105"
              >
                View My Projects
              </Link>
              <Link
                to="/resume"
                className="w-full sm:w-auto bg-gray-200 text-gray-800 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-all duration-300 ease-in-out"
              >
                See My Resume
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Feature Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">What I Do</h2>
            <p className="mt-3 text-lg text-gray-600">A quick look at my passions and professional services.</p>
          </div>

          {/* 3-Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Technology */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 flex flex-col justify-between p-8">
              <div>
                <div className="flex items-center justify-center w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full mb-5">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5 0c.472 0 .848.376.848.848V16.5a.848.848 0 01-.848.848H8.348a.848.848 0 01-.848-.848V7.598c0-.472.376-.848.848-.848h5.804z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Technology &amp; Development</h3>
                <p className="text-gray-600 mb-6">Building clean, useful tools and exploring system architecture on the web.</p>
              </div>
              <Link to="/projects" className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors">
                View Projects &rarr;
              </Link>
            </div>

            {/* Card 2: Language */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 flex flex-col justify-between p-8">
              <div>
                <div className="flex items-center justify-center w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full mb-5">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c.506 0 1.023.042 1.531.127M12 21c-.506 0-1.023.042-1.531.127M12 3a9.004 9.004 0 00-8.716 6.747M12 3a9.004 9.004 0 018.716 6.747M12 3c.506 0 1.023-.042 1.531.127M12 3c-.506 0-1.023-.042-1.531.127m0 0A48.11 48.11 0 0112 5.25c.618 0 1.221.043 1.808.128m-3.616 0A48.11 48.11 0 0012 5.25c-.618 0-1.221.043-1.808.128m3.616 0A48.11 48.11 0 0112 5.25c.618 0 1.221.043 1.808.128M18 9.75l-6 6-6-6" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Linguistics &amp; Communication</h3>
                <p className="text-gray-600 mb-6">Fascinated by the structure of communication and natural language.</p>
              </div>
              <Link to="/resume" className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors">
                See My Bio &rarr;
              </Link>
            </div>

            {/* Card 3: Dashboard/Scheduling */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 flex flex-col justify-between p-8">
              <div>
                <div className="flex items-center justify-center w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full mb-5">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Dashboard &amp; Scheduling</h3>
                <p className="text-gray-600 mb-6">Use this site as a hub. Schedule a meeting, visit the shop, or make a payment.</p>
              </div>
              <Link to="/contact" className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors">
                Get in Touch &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
